/**
 * BusinessDetail.UI.js
 * Componentes UI primitivos reutilizables de la pantalla de detalle de negocio.
 */
import React from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Copy, Loader2 } from "lucide-react";

// ─── InfoRow ──────────────────────────────────────────────────────────────────
export function InfoRow({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-300">
      <span className="text-gray-500 flex-shrink-0">{icon}</span>
      <span className="truncate">{text}</span>
    </div>
  );
}

// ─── Section (collapsible card) ───────────────────────────────────────────────
export function Section({ icon, title, subtitle, done, expanded, onToggle, children }) {
  return (
    <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-white/[0.03] transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? "bg-green-900/50" : "bg-white/5"}`}>
            <span className={done ? "text-green-400" : "text-gray-500"}>
              {done ? <CheckCircle2 size={14} /> : icon}
            </span>
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-white">{title}</p>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>
        {expanded ? <ChevronUp size={15} className="text-gray-600" /> : <ChevronDown size={15} className="text-gray-600" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/[0.06] pt-3">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── ActionButton ─────────────────────────────────────────────────────────────
export function ActionButton({ testId, loading, onClick, label, icon, variant = "primary", loadingLabel }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={loading}
      className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
        variant === "primary"
          ? "bg-[#ffc700] text-[#0A0A0A] hover:bg-yellow-400 disabled:opacity-60"
          : "bg-white/[0.07] text-gray-300 hover:bg-white/[0.12] disabled:opacity-60"
      }`}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : icon}
      {loading ? (loadingLabel || "Procesando...") : label}
    </button>
  );
}

// ─── ReportItem ───────────────────────────────────────────────────────────────
export function ReportItem({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-gray-200 leading-relaxed">{value}</p>
    </div>
  );
}

// ─── ScriptBlock ──────────────────────────────────────────────────────────────
export function ScriptBlock({ label, value, onCopy, highlight, small }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-[#ffc700]/10 border border-[#ffc700]/20" : "bg-white/[0.05] border border-white/[0.07]"}`}>
      <div className="flex justify-between items-center mb-1.5">
        <p className={`text-[10px] font-bold uppercase tracking-wide ${highlight ? "text-[#ffc700]" : "text-gray-500"}`}>
          {label}
        </p>
        <button onClick={onCopy} className={`${highlight ? "text-[#ffc700]/60 hover:text-[#ffc700]" : "text-gray-600 hover:text-gray-300"} transition-colors`}>
          <Copy size={11} />
        </button>
      </div>
      <p className={`text-xs leading-relaxed whitespace-pre-wrap ${highlight ? "text-white font-semibold text-sm" : "text-gray-300"}`}>
        {value}
      </p>
    </div>
  );
}
